/* eslint-disable */
import axios from 'axios'
import {API_URL, API_TIMEOUT} from '@/config'
import {mapActions} from 'vuex'
import SessionService from '@/services/SessionService'
import {LOGIN_URL, SITE_NAME} from '@/config'

const ApiService = {
  ...mapActions('snackbar', [
    'showSnackbar'
  ]),
  ...mapActions('loading', [
    'setLoading',
    'unsetLoading'
  ]),

  init(store, router) {
    // To make actions work
    // noinspection JSUnusedGlobalSymbols
    this.$store = store
    this.$router = router
    this.timeoutId = 0

    axios.interceptors.request.use(
      config => {
        config.timeout = API_TIMEOUT
        if (SessionService.isLoggedIn()) {
          config.headers['Authorization'] = `Bearer ${SessionService.getToken()}`
        }
        return config
      }
    )

    axios.interceptors.response.use(
      response => response,
      async error => {
        if (!!error && !!error.response && error.response.status === 401) {
          this.checkVersion(error.response.headers, true)
          location.href = `${LOGIN_URL}?nombre=${SITE_NAME}&redirect=${location.href}`
        }
        return Promise.reject(error)
      }
    )
  },

  handleError(error) {
    let temporaryError = false
    // Network Errors
    if (error.message === 'Network Error') {
      temporaryError = true
      this.showSnackbar(['Compruebe su conexión a internet y vuelva a intentar'])
      console.log(`Error de red: ${error}`)
      // Unauthorized
    } else if (error.response && error.response.status === 401) {
      // If is not authorized, avoid unnecessary snackbar
      console.log(`Sesión caducada o token no autorizado: ${error}`)
      // Server (Backend) Errors
    } else if (error.response && (error.response.data || error.response.data === '')) {
      // Marked as Error by Backend Developer
      if (error.response.data.estado === false) {
        if (error.response.data.mensaje === 'La sesión del usuario ha caducada.') {
          this.$router.push({name: 'ElegirEntidad'})
          return
        } else {
          this.showSnackbar([error.response.data.mensaje])
          console.log(`BackendError: ${error.response.data.mensaje}`)
        }
      } else {
        // Other Standard Server Errors
        if (error.response.status === 404) {
          this.showSnackbar(['Error inesperado. Favor intente más tarde.'])
          console.log(`Not found: ${error}`)
        } else if (error.response.status <= 499) {
          this.showSnackbar(['Error inesperado. Favor intente más tarde.'])
          console.log(`Client error: ${error}`)
        } else if (error.response.status === 503) {
          this.showSnackbar(['Servicio en mantenimiento. Vuelva a intentar más tarde.'])
        } else if (error.response.status >= 500) {
          this.showSnackbar(['Error inesperado. Favor intente más tarde.'])
          console.log(`Internal server Error: ${error}`)
          // Could be a Gateway Timeout e.g. from Nginx (only if status > 500)
          temporaryError = error.response.status > 500
        }
      }
      // Unknown Errors
    } else {
      this.showSnackbar(['Error inesperado. Favor intente más tarde.'])
      console.log(`Error desconocido: ${error}`)
    }

    if (!!error && !!error.response && !!error.response.headers) {
      this.checkVersion(error.response.headers)
    }

    error.name = 'FhNetworkError'
    if (temporaryError) {
      error.name = 'FhTemporaryError'
    }
    throw error
  },

  handleResponse(response) {
    this.checkVersion(response.headers)

    if (response.data.estado !== true) {
      let e = new Error(response.data.mensaje)
      e.name = 'FhBackendError'
      e.response = response
      throw e
    }
    return response.data.resultado
  },

  checkVersion (headers, forceReload=false) {
    let serverVersion = headers['x-spa-version']
    let spaVersion = process.env.VUE_APP_SPA_VERSION

    if (serverVersion && spaVersion && serverVersion !== spaVersion) {
      // Permitir que otras notificaciones puedan ser mostradas antes que esta
      setTimeout(() => {
        this.showSnackbar(['Nueva version disponible', 'success', 'Actualizar', () => window.location.reload()])
      }, 200)

      if (forceReload) {
        // Recargar la página una sola vez para la misma versión
        let lastServerVersion = localStorage.getItem('version')
        if (!!lastServerVersion && lastServerVersion === serverVersion) {
          return
        }
        localStorage.setItem('version', serverVersion)

        window.location.reload()

        // Prevenir procesamiento subsiguiente
        throw Error('Nueva version disponible. Por favor recargue la página.')
      }
    }
  },

  async get(resource, params = {}) {
    let loadId = 'get:' + resource
    this.setLoading(loadId)

    try {
      let resp = await axios.get(`${API_URL}/${resource}`, {params})
      return this.handleResponse(resp)
    } catch (error) {
      this.handleError(error)
    } finally {
      this.unsetLoading(loadId)
      // this.postponeAutologout()
    }
  },

  async post(resource, data) {
    let loadId = 'post:' + resource
    this.setLoading(loadId)

    try {
      let resp = await axios.post(`${API_URL}/${resource}`, data)
      return this.handleResponse(resp)
    } catch (error) {
      this.handleError(error)
    } finally {
      this.unsetLoading(loadId)
      // this.postponeAutologout()
    }
  },

  async delete (resource, params = {}) {
    let loadId = 'delete:' + resource
    this.setLoading(loadId)

    try {
      let resp = await axios.delete(`${API_URL}/${resource}`, {params})
      return this.handleResponse(resp)
    } catch (error) {
      this.handleError(error)
    } finally {
      this.unsetLoading(loadId)
    }
  },

  redirectToLogin() {
    window.location.href = `${LOGIN_URL}?nombre=${SITE_NAME}&redirect=${window.location.href}`
  },

  postponeAutologout() {
    if (this.timeoutId) {
      window.clearTimeout(this.timeoutId)
    }

    let minutosValidezSesion = this.$store.getters['configuracion/getMinutosValidezSesion']
    this.timeoutId = window.setTimeout(this.redirectToLogin, minutosValidezSesion * 60 * 1000)
  },
}

export default ApiService
